"use client";

import type { KioskPricingMap } from "@frontend/components/priceToggle/usePriceMap";
import {
  getCategories,
  getProductsByCategoryId,
} from "@frontend/lib/actions/actions";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import type { InsurancesStatusEnum } from "@frontend/lib/graphql/generated-gql";
import { ErrorCode } from "@frontend/lib/utils/insuranceErrorResolver";
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useKioskSafe } from "../(render)/kiosk-provider";
import { getStateIdFromUrl } from "../devtool/playwright-generator";
import { traceInspector } from "../devtool/trace-inspector";
import { domEventSpy } from "../lib/dom-event-spy";
import type { HrtType } from "../machine/actors/load-hrts.actor";
import type { KioskMachineEvent } from "../machine/types";
import { getKioskPrices } from "../server-actions/pricing-actions";

interface KioskEventContextValue {
  send: (event: KioskMachineEvent) => void;
  pricingMap: KioskPricingMap;
  isPricingPending?: boolean;
  remainingHrtIds: number[];
  isPendingPricesMode: boolean;
}

const KioskEventContext = createContext<KioskEventContextValue | null>(null);

/**
 * Provider that exposes kiosk event sending and pricing map to child components.
 * Gracefully handles being rendered outside of KioskProvider (e.g., in Storybook)
 * by simply rendering children without the context.
 */
export function KioskEventProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const kioskContext = useKioskSafe();

  // If not inside KioskProvider (e.g., Storybook), just render children
  // The useKioskEvent hook will return isKioskMode: false
  if (!kioskContext) {
    return <>{children}</>;
  }

  const context = kioskContext.state.context;
  const isStorefrontBrowse = kioskContext.state.matches("storefrontBrowse");

  // Track if component has mounted (hydration complete)
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Extract pricing params from context
  const defaultHrt = context.defaultHrt as { hrtId: number } | null;
  const epsHrt = context.epsHrt as { hrtId: number } | null;
  const workersCompHrt = context.workersCompHrt as { hrtId: number } | null;
  const travelInsuranceHrt = context.travelInsuranceHrt as {
    hrtId: number;
  } | null;
  const defaultHrtId = defaultHrt?.hrtId;

  const allHrtIds = context.hrts?.map((hrt: HrtType) => hrt.hrtId) ?? [];
  const remainingHrtIds = allHrtIds.filter(
    (id: number) =>
      id !== epsHrt?.hrtId &&
      id !== workersCompHrt?.hrtId &&
      id !== travelInsuranceHrt?.hrtId,
  );

  // Self-pay: patient chose "No insurance" in triage — ignore cached
  // insurance data that may still be in context from a previous pass.
  // Exception: insurance was added from the storefront during this session —
  // the triage answer predates it and must not suppress insured pricing.
  const isSelfPay =
    context.planToUseInsurance === false &&
    context.addedInsuranceFromStorefront !== true;

  const insurances = isSelfPay
    ? undefined
    : (context.insurances as {
        primaryInsurance?: {
          payerId: string;
          payer: string;
          memberId: string;
          state: string;
          status?: InsurancesStatusEnum;
          error?: ErrorCode | null;
        };
        secondaryInsurance?: {
          payerId: string;
          payer: string;
          memberId: string;
          state: string;
          status?: InsurancesStatusEnum;
          error?: ErrorCode | null;
        };
      });

  // Prefetch categories to prime cache for child components
  const { data: categories } = useQuery({
    queryKey: ["categories", context.orgId],
    queryFn: () => getCategories(context.orgId as number),
    enabled: isStorefrontBrowse && !!context.orgId,
    staleTime: 5 * 60 * 1000,
  });

  // Prefetch products based on categories
  const { data: products } = useQuery({
    queryKey: ["products", categories?.map((c) => c.id)],
    queryFn: () =>
      getProductsByCategoryId(categories?.map((category) => category.id) || []),
    enabled: isStorefrontBrowse && !!categories?.length,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Prefetch organization assets
  useQuery({
    queryKey: ["organizationAssets", context.orgId],
    queryFn: () => getOrganizationAssetsForOrg(context.orgId as number),
    enabled: isStorefrontBrowse && !!context.orgId,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch pricing when in storefrontBrowse state
  const {
    data: pricingData,
    isLoading: isPricingLoading,
    isPending: isPricingPending,
  } = useQuery({
    queryKey: [
      "storefront-pricing",
      context.orgId,
      remainingHrtIds,
      context.consumerId,
      context.pendingPricesMode,
      // Use insuranceInputSnapshot as cache key - when inputs change, this changes,
      // creating a new query key with no cached data (forces loading state)
      context.insuranceInputSnapshot,
      context.firstName,
      context.lastName,
      context.insuranceStates,
      context.planToUseInsurance,
      context.addedInsuranceFromStorefront === true,
    ],
    queryFn: async () => {
      const consumer = {
        id: (context.consumerId as string) ?? "",
        fname: (context.firstName as string) ?? "",
        lname: (context.lastName as string) ?? "",
        dob: (context.dateOfBirth as string) ?? "",
      };

      // Filter insurances based on insurance states - only include active, verified insurances
      type InsuranceState = {
        verified?: boolean;
        active?: boolean;
        inNetwork?: boolean;
      };
      type InsuranceStates = {
        primary?: InsuranceState;
        secondary?: InsuranceState;
      };
      const contextAny = context as {
        insuranceError?: { insuranceStates?: InsuranceStates };
        insuranceStates?: InsuranceStates;
      };
      const insuranceStates =
        contextAny.insuranceError?.insuranceStates ||
        contextAny.insuranceStates;

      const validInsurances: Array<{
        payer: string;
        memberId: string;
        state: string;
        status?: string;
      }> = [];
      if (insurances?.primaryInsurance) {
        const primaryState = insuranceStates?.primary;

        // Include primary only if it's verified, active, and in-network.
        // OON insurance (inNetwork: false) must not trigger an insured price
        // fetch — the insurancesArray filter below would strip it out anyway,
        // leaving insuredHrtIds populated but insurances empty, which causes
        // the pricing API to return inconsistent results intermittently.
        if (
          !primaryState ||
          (primaryState.verified &&
            primaryState.active &&
            primaryState.inNetwork !== false)
        ) {
          validInsurances.push(insurances.primaryInsurance);
        }
      }
      if (insurances?.secondaryInsurance) {
        const secondaryState = insuranceStates?.secondary;
        // Include secondary only if it's verified, active, and in-network
        if (
          secondaryState?.verified &&
          secondaryState?.active &&
          secondaryState.inNetwork !== false
        ) {
          validInsurances.push(insurances.secondaryInsurance);
        }
      }

      // Check if we should fetch insured prices
      // Don't fetch insured prices if in pending prices mode or if no valid insurances
      const shouldFetchInsuredPrices =
        context.pendingPricesMode || validInsurances.length > 0;

      // Determine which HRTs to price as insured vs cash
      // Use remainingHrtIds (excludes EPS, workers comp, travel insurance — they have dedicated pricing flows)
      const insuredHrtIds = shouldFetchInsuredPrices ? remainingHrtIds : [];
      const cashHrtIds = remainingHrtIds;

      const insurancesArray = [
        insurances?.primaryInsurance,
        insurances?.secondaryInsurance,
      ]
        .filter(
          (
            insurance,
            index,
            arr,
          ): insurance is NonNullable<typeof insurance> => {
            if (insurance === undefined || insurance === null) return false;

            // Only filter out secondary (index 1) if unverified AND we have a primary
            const isSecondary = index === 1;
            const isUnverified =
              insurance.status?.toLowerCase() === "unverified";
            const hasPrimary = arr[0] !== undefined && arr[0] !== null;

            if (isSecondary && isUnverified && hasPrimary) {
              return false;
            }

            return true;
          },
        )
        .filter(
          (insurance) =>
            !insurance.error || insurance.error !== ErrorCode.OUT_OF_NETWORK,
        );

      // Get stateId from URL for S3 fixture upload (test replay)
      const fixtureId = getStateIdFromUrl();

      const pricingMap = await getKioskPrices(
        insuredHrtIds, // Only pass HRTs if NOT in pending prices mode
        cashHrtIds, // Always pass all HRTs for cash pricing
        consumer,
        insurancesArray,
        context.orgId as number,
        (context.email as string) ?? "",
        (context.firstName as string) ?? "",
        (context.lastName as string) ?? "",
        (context.dateOfBirth as string) ?? "",
        Number.parseInt((context.practiceId as string) ?? "0"),
        undefined,
        (context.pendingPricesMode as boolean) ?? false,
        fixtureId,
      );

      return pricingMap;
    },
    enabled:
      isStorefrontBrowse &&
      !!context.firstName &&
      !!context.lastName &&
      !!context.dateOfBirth &&
      remainingHrtIds.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Update machine context when pricing loads
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (pricingData && isStorefrontBrowse) {
      kioskContext.send({
        type: "SET_PRICING_MAP",
        data: { newPricingMap: pricingData },
      });
    }
  }, [pricingData, isStorefrontBrowse]);

  // Wrap send to flush DOM events before sending machine events
  // This ensures storefront component interactions are captured in the trace
  const wrappedSend = useCallback(
    (event: KioskMachineEvent) => {
      // Flush captured DOM events and attach to trace BEFORE machine event fires
      const domEvents = domEventSpy.flush();
      if (domEvents.length > 0) {
        traceInspector.addUIHint({
          component: "StorefrontComponent",
          action: "click",
          domEvents,
        });
      }
      kioskContext.send(event);
    },
    [kioskContext],
  );

  return (
    <KioskEventContext.Provider
      value={{
        send: wrappedSend,
        pricingMap: kioskContext.state.context.pricingMap as KioskPricingMap,
        isPricingPending,
        remainingHrtIds,
        isPendingPricesMode: context.pendingPricesMode ?? false,
      }}
    >
      {children}
    </KioskEventContext.Provider>
  );
}

export function useKioskEvent() {
  const context = useContext(KioskEventContext);

  if (!context) {
    // Not in kiosk mode - return no-op for graceful degradation
    return {
      send: (() => {}) as (event: KioskMachineEvent) => void,
      pricingMap: {},
      isPricingPending: false,
      remainingHrtIds: [] as number[],
      isPendingPricesMode: false,
      getShouldShowPricingLoading: (_hrtIds: number[], _price: number) => false,
    };
  }

  const getShouldShowPricingLoading = (hrtIds: number[], price: number) => {
    const hrtsBeingPriced = context.remainingHrtIds.filter((id) =>
      hrtIds.includes(id),
    );
    const hasSomeNoPriceAvailable = hrtsBeingPriced.some((hrtId) => {
      const priceEntry = context.pricingMap?.[hrtId];
      return priceEntry?.priceData.INSURED === undefined;
    });

    return (
      (context.isPricingPending && hasSomeNoPriceAvailable) ||
      (price === 0 && hasSomeNoPriceAvailable && hrtsBeingPriced.length > 0)
    );
  };

  return {
    ...context,
    getShouldShowPricingLoading,
  };
}
